/**
 * @module useScrollTracking
 * @description Hook to track when a user scrolls at all (fires a pageScrolled event with scrollDistance=1)
 */
import { useEffect, useRef, useCallback } from "react";
import { useRouter } from "next/router";
import { trackEvent } from "@/js/helpers/tracking";

/**
 * @function useScrollTracking
 * @description Fires a pageScrolled event with scrollDistance=1 when user scrolls for the first time on each page
 */
export const useScrollTracking = () => {
  const router = useRouter();
  const hasScrolled = useRef(false);
  const listenerRef = useRef(null);

  const attachScrollListener = useCallback(() => {
    if (listenerRef.current) return;
    const handleScroll = () => {
      if (!hasScrolled.current && window.scrollY > 0) {
        hasScrolled.current = true;
        trackEvent({ action: "pageScrolled", data: { scrollDistance: 1 } });
        // Remove listener after firing to reduce scroll handler overhead
        window.removeEventListener("scroll", handleScroll);
        listenerRef.current = null;
      }
    };

    listenerRef.current = handleScroll;
    window.addEventListener("scroll", handleScroll, { passive: true });
  }, []);

  // Reset scroll tracking on route change and re-attach listener
  useEffect(() => {
    const handleRouteChange = () => {
      hasScrolled.current = false;
      attachScrollListener();
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events, attachScrollListener]);

  // Attach listener on initial mount
  useEffect(() => {
    attachScrollListener();

    return () => {
      if (listenerRef.current) {
        window.removeEventListener("scroll", listenerRef.current);
        listenerRef.current = null;
      }
    };
  }, [attachScrollListener]);
};
